<template>
  <v-card>
    <v-card-title>
      <span>{{ $t('reports_ui.fleet_health_report.daily_stats') }}</span>
      <info-modal>
        <v-card-title>
          {{ $t('reports_ui.fleet_health_report.daily_stats') }}
        </v-card-title>
        <v-card-text v-html="$t('reports_ui.fleet_health_report.modal_info.daily_stats')" />
      </info-modal>
    </v-card-title>
    <v-card-text>
      <v-data-table
        dense
        class="vf-table"
        style="width: 100%"
        :items="items"
        :headers="fields"
        :current-page.sync="currentPage"
        :footer-props="{ 'items-per-page-options': pageOptions}"
        :items-per-page.sync="rowsPerPage">
        <template #header>
          <thead>
            <tr>
              <th :colspan="1"/>
              <th class="border-left border-right text-center" colspan="2">
                <span class="text-subtitle-2">
                  {{ $t('reports_ui.fleet_health_report.vehicle_health') }}
                </span>
              </th>
              <th class="border-right text-center" colspan="2">
                <span class="text-subtitle-2">
                  {{ $t('reports_ui.fleet_health_report.violations_past_30_days') }}
                </span>
              </th>
              <th class="border-right text-center" colspan="6">
                <span class="text-subtitle-2">
                  {{ $t('reports_ui.fleet_health_report.open_vehicle_issues') }}
                </span>
              </th>
              <th class="border-right text-center" colspan="2">
                <span class="text-subtitle-2">
                  {{ $tc('vehicle_issue', 2) }}
                </span>
              </th>
            </tr>
          </thead>
        </template>
        <template #item.num_healthy="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.num_unhealthy="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.hot_violations="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.cold_violations="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.sensors_swapped="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.gps_failure="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.cooler_sensor_failure="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.freezer_sensor_failure="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.cooler_temperature_health="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.freezer_temperature_health="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.average_issue_age_in_days="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
        <template #item.max_issue_age_in_days="data">
          <if-set :value="data.value" dash>
            {{ formatNum(data.value) }}
          </if-set>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoModal from '@/components/shared/InfoModal'
import IfSet from '@/components/shared/IfSet'
import { map, round } from 'lodash'
import Moment from 'moment-timezone'

export default {
  name: 'DailyStats',
  components: {
    InfoModal,
    IfSet
  },
  props: {
    customerReport: {
      type: Object
    }
  },
  data: function () {
    return {
      currentPage: 1,
      rowsPerPage: 10,
      sortBy: null,
      sortDesc: false,
      pageOptions: [10, 20, 50, 100]
    }
  },
  computed: {
    /**
     * The total number of results for the table
     */
    totalRows () {
      return this.items.length
    },
    /**
     * The list of items to display in the table. Convert from a mapped object into an array, and add the keyed date
     * as a field.
     */
    items () {
      return map(this.customerReport.insights_dashboard_history, (value, key) => {
        // Unfortunate hack due to some poor design on my part.
        // In the backend report generator, PHP will serialize days with data into JSON objects, but days with no data
        // will become empty arrays. This apparently wasn't a problem in the bootstrap table, but Vuetify doesn't like
        // seeing these empty arrays and will throw an error.
        //
        // One obvious hack was to just always set the date serverside (thus negating any need to do the operation
        // below), but it turns out we depend on the entries being completely empty elsewhere. Additionally,
        // this would break old reports as well.
        //
        // So instead, let's just check for empty arrays and manually convert them into empty objects.
        if (Array.isArray(value)) {
          value = {}
        }
        value.date = Moment(key).format('ll')
        return value
      })
    },
    /**
     * The fields of the table.
     */
    fields () {
      return [
        {
          value: 'date',
          text: this.$t('reports_ui.fleet_health_report.snapshot_date'),
          sortable: false
        },
        {
          value: 'num_healthy',
          text: this.$t('reports_ui.fleet_health_report.num_healthy'),
          sortable: false,
          class: 'wrap border-left'
        },
        {
          value: 'num_unhealthy',
          text: this.$t('reports_ui.fleet_health_report.num_unhealthy'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'hot_violations',
          text: this.$t('hot_violations'),
          sortable: false,
          class: 'wrap border-left'
        },
        {
          value: 'cold_violations',
          text: this.$t('cold_violations'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'sensors_swapped',
          text: this.$t('sensors_swapped'),
          sortable: false,
          class: 'wrap border-left'
        },
        {
          value: 'gps_failure',
          text: this.$t('fleet_health_ui.gps_failure'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'cooler_sensor_failure',
          text: this.$t('fleet_health_ui.cooler_sensor_failure'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'freezer_sensor_failure',
          text: this.$t('fleet_health_ui.freezer_sensor_failure'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'cooler_temperature_health',
          text: this.$t('cooler_temperature_health_warning'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'freezer_temperature_health',
          text: this.$t('freezer_temperature_health_warning'),
          sortable: false,
          class: 'wrap'
        },
        {
          value: 'average_issue_age_in_days',
          text: this.$t('reports_ui.fleet_health_report.average_issue_age_in_days'),
          sortable: false,
          class: 'wrap border-left'
        },
        {
          value: 'max_issue_age_in_days',
          text: this.$t('reports_ui.fleet_health_report.max_issue_age_in_days'),
          sortable: false,
          class: 'wrap'
        }
      ]
    },
    /**
     * @return number
     */
    from () {
      return (this.rowsPerPage * (this.currentPage - 1)) + 1
    },
    /**
     * @return number
     */
    to () {
      return this.rowsPerPage * this.currentPage
    }
  },
  methods: {
    formatNum (avg) {
      return round(avg, 1).toLocaleString()
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
